import type { Stroke, Point } from '../../types';
import type { HandlePosition, Handle, Bounds } from '../diagram/state/types';
import { getViewportScale } from '../diagram/shapes/selectionRender';

export function getStrokeBounds(stroke: Stroke): Bounds {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of stroke.points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function getStrokeHandles(stroke: Stroke, padding = 0): Handle[] {
  const b = getStrokeBounds(stroke);
  const x0 = b.x - padding;
  const y0 = b.y - padding;
  const x1 = b.x + b.width + padding;
  const y1 = b.y + b.height + padding;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  return [
    { position: 'top-left', point: { x: x0, y: y0 } },
    { position: 'top', point: { x: mx, y: y0 } },
    { position: 'top-right', point: { x: x1, y: y0 } },
    { position: 'right', point: { x: x1, y: my } },
    { position: 'bottom-right', point: { x: x1, y: y1 } },
    { position: 'bottom', point: { x: mx, y: y1 } },
    { position: 'bottom-left', point: { x: x0, y: y1 } },
    { position: 'left', point: { x: x0, y: my } },
  ];
}

const HANDLE_SIZE = 12;

export function getStrokeHandleAtPoint(stroke: Stroke, point: Point, scale = 1): HandlePosition | null {
  const handles = getStrokeHandles(stroke);
  const threshold = HANDLE_SIZE / scale;
  for (const handle of handles) {
    const dist = Math.hypot(point.x - handle.point.x, point.y - handle.point.y);
    if (dist <= threshold) return handle.position;
  }
  return null;
}

const MIN_SIZE = 10;

export function resizeStroke(stroke: Stroke, handle: HandlePosition, newHandlePoint: Point): void {
  const old = getStrokeBounds(stroke);
  if (old.width === 0 && old.height === 0) return;

  // Compute new bounds from the handle being dragged
  let nx = old.x, ny = old.y, nw = old.width, nh = old.height;

  switch (handle) {
    case 'top-left':
      nx = newHandlePoint.x; ny = newHandlePoint.y;
      nw = old.x + old.width - newHandlePoint.x;
      nh = old.y + old.height - newHandlePoint.y;
      break;
    case 'top':
      ny = newHandlePoint.y;
      nh = old.y + old.height - newHandlePoint.y;
      break;
    case 'top-right':
      ny = newHandlePoint.y;
      nw = newHandlePoint.x - old.x;
      nh = old.y + old.height - newHandlePoint.y;
      break;
    case 'right':
      nw = newHandlePoint.x - old.x;
      break;
    case 'bottom-right':
      nw = newHandlePoint.x - old.x;
      nh = newHandlePoint.y - old.y;
      break;
    case 'bottom':
      nh = newHandlePoint.y - old.y;
      break;
    case 'bottom-left':
      nx = newHandlePoint.x;
      nw = old.x + old.width - newHandlePoint.x;
      nh = newHandlePoint.y - old.y;
      break;
    case 'left':
      nx = newHandlePoint.x;
      nw = old.x + old.width - newHandlePoint.x;
      break;
  }

  // Enforce minimum size
  if (Math.abs(nw) < MIN_SIZE) nw = nw < 0 ? -MIN_SIZE : MIN_SIZE;
  if (Math.abs(nh) < MIN_SIZE) nh = nh < 0 ? -MIN_SIZE : MIN_SIZE;

  // Map all points from old bounds to new bounds
  const ow = old.width || 1;
  const oh = old.height || 1;

  for (const p of stroke.points) {
    const tx = (p.x - old.x) / ow;
    const ty = (p.y - old.y) / oh;
    p.x = nx + tx * nw;
    p.y = ny + ty * nh;
  }
}

export function renderStrokeSelection(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  _scale: number,
): void {
  const scale = getViewportScale(ctx);
  const bounds = getStrokeBounds(stroke);

  ctx.save();

  // Dashed bounding box — matches DrawShape.renderSelection
  ctx.strokeStyle = '#0066ff';
  ctx.lineWidth = 2 / scale;
  ctx.setLineDash([5 / scale, 5 / scale]);
  ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
  ctx.setLineDash([]);

  // 8 handle circles
  const handles = getStrokeHandles(stroke);
  for (const h of handles) {
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#0066ff';
    ctx.lineWidth = 2 / scale;
    ctx.beginPath();
    ctx.arc(h.point.x, h.point.y, (HANDLE_SIZE / 2) / scale, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}
